import cn from 'classnames'
import Image from 'next/image'

import { Box } from '@features/story/components/Box'
import { formatDateDomini, abbreviateNumber } from '@lib/utils'
import { BookmarkSolidIcon } from '@components/icons/BookmarkSolidIcon'
import { ChatboxIcon } from '@components/icons/ChatboxIcon'
import { EyeIcon } from '@components/icons/EyeIcon'
import { ChapterManageListProps } from './interface'
import { Status } from '../Status'
import { ReadFree } from '../ReadFree'
import { CheckboxInput } from '@components/CheckboxInput'
import { promotionDate } from '@lib/promotion/utils'

export const ChapterManageList = ({
  data,
  order,
  isRestricted,
  isManga,
  isDrawer,
  isSelect,
  selectedChapters,
  handleSelectChapter,
  isBuyAllChapterFetching,
  bookReadPromotionDetail,
  isReadFreePromotion,
  handleClickChapter,
  buyable,
}: ChapterManageListProps) => {
  const now = new Date()
  const hasActiveBookChapterCampaign =
    data.chapterCampaign &&
    new Date(data.chapterCampaign.startedAt) <= now &&
    new Date(data.chapterCampaign.endedAt) >= now

  return (
    <div
      onClick={e => {
        e.stopPropagation()

        if (!isSelect) {
          handleClickChapter()
          return
        }

        if (buyable) {
          handleSelectChapter?.(data)
        }
      }}
      className={cn(
        'flex space-x-[10px] mobile:space-x-0 desktop-tablet:items-center mobile:flex-col py-[10px] mobile:px-[10px] px-5 bg-white dark:bg-dark-8 hover:bg-blue-2 dark:hover:bg-dark border-b border-gray-3 dark:border-purple cursor-pointer min-h-[70px]',
        {
          'cursor-not-allowed': isRestricted || isBuyAllChapterFetching,
        }
      )}
      data-test={`chapter-${order}`}
    >
      <div
        className={cn('flex items-center mobile:hidden space-x-[20px]', {
          'w-fit': isDrawer,
          'w-full': !isDrawer,
        })}
      >
        {isSelect ? (
          <CheckboxInput
            readOnly
            className={cn('!w-fit', {
              '[&_input]:invisible': !buyable,
              'pointer-events-none': buyable,
            })}
            name={`chapter.${data.id}`}
            disabled={!buyable}
            checked={
              selectedChapters?.length
                ? selectedChapters?.findIndex(
                    chapter => chapter.id === data.id
                  ) !== -1
                : false
            }
          >
            <Box.Name
              className={cn('w-[48px] text-secondary-1 font-bold shrink-0', {
                '!text-primary': data.isBookmarked,
              })}
            >
              {order.toLocaleString()}
            </Box.Name>
          </CheckboxInput>
        ) : (
          <Box.Name
            className={cn('w-[48px] text-secondary-1 font-bold shrink-0', {
              '!text-primary': data.isBookmarked,
            })}
          >
            {order.toLocaleString()}
          </Box.Name>
        )}

        {isManga && (
          <div
            className={cn(
              'shrink-0 w-[50px] h-[50px] rounded-[8px] overflow-hidden relative bg-gray-3',
              {
                'opacity-30': isRestricted,
              }
            )}
          >
            {data.chapterCoverImgPath ? (
              <Image
                src={data.chapterCoverImgPath}
                layout='fill'
                alt={`${data.chapterTitle} ${data.chapterName}`}
              />
            ) : null}
          </div>
        )}

        <div
          className={cn({
            'opacity-30': isRestricted,
          })}
        >
          <div
            className={cn(
              'flex items-center space-x-[20px] tablet:space-x-[10px] tablet:mb-[4px]',
              {
                'w-[170px]': isDrawer && !isManga,
                'w-[100px]': isDrawer && isManga,
              }
            )}
          >
            {data.isBookmarked && (
              <BookmarkSolidIcon
                className='text-primary min-w-[18px] min-h-[18px]'
                width='18'
                height='18'
              />
            )}
            <Box.Text
              className={cn(
                'text-sm w-full flex leading-[1.8] items-center tablet:space-x-[10px]',
                {
                  'text-primary font-bold space-x-[20px]': data.isBookmarked,
                  'space-x-[28px] dark:text-dark-10': !data.isBookmarked,
                }
              )}
            >
              {/* <span className='line-clamp-1 min-w-[50px]'> */}
              {/*  {data.chapterTitle} */}
              {/* </span> */}
              <span className='line-clamp-1 break-all'>
                {data.chapterTitle} {data.chapterName}
              </span>
            </Box.Text>
          </div>

          <Box.Text className='text-sm text-secondary-1 desktop:hidden'>
            {formatDateDomini(data.updatedAtOrPublishedAt, false, false)}
          </Box.Text>
        </div>
      </div>

      <div className='flex space-x-[20px] w-full desktop-tablet:hidden mobile:space-x-[16px]'>
        {isManga && (
          <div
            className={cn(
              'shrink-0 w-[50px] h-[50px] rounded-[8px] overflow-hidden relative bg-gray-3',
              {
                'opacity-30': isRestricted,
              }
            )}
          >
            {data.chapterCoverImgPath ? (
              <Image
                src={data.chapterCoverImgPath}
                layout='fill'
                alt={`${data.chapterTitle} ${data.chapterName}`}
              />
            ) : null}
          </div>
        )}
        <div className='flex flex-col w-full'>
          <div className={cn('flex space-x-[10px] justify-between')}>
            {isSelect && buyable ? (
              <CheckboxInput
                readOnly
                className={cn('!w-fit', {
                  '[&_input]:invisible': !buyable,
                  'pointer-events-none': buyable,
                })}
                name={`chapter.${data.id}`}
                disabled={!buyable}
                checked={
                  selectedChapters?.length
                    ? selectedChapters?.findIndex(
                        chapter => chapter.id === data.id
                      ) !== -1
                    : false
                }
              >
                <Box.Name
                  className={cn('text-secondary-1', {
                    '!text-primary': data.isBookmarked,
                  })}
                >
                  {order.toLocaleString()}
                </Box.Name>
              </CheckboxInput>
            ) : (
              <Box.Name
                className={cn('text-secondary-1', {
                  '!text-primary': data.isBookmarked,
                })}
              >
                {order.toLocaleString()}
              </Box.Name>
            )}
            {hasActiveBookChapterCampaign && !data.isPaid && (
              <Box.Text
                className={cn(
                  'h-fit whitespace-nowrap text-xs py-1 rounded px-2 text-white dark:text-white-2 min-w-[140px] text-center desktop:hidden',
                  {
                    'bg-red-3': data.chapterCampaign.sellingPrice > 0,
                    'bg-blue-1': data.chapterCampaign.sellingPrice <= 0,
                  }
                )}
              >
                {data.chapterCampaign.sellingPrice > 0 ? 'ลดราคา' : 'อ่านฟรี'}{' '}
                {promotionDate(
                  data.chapterCampaign.startedAt,
                  data.chapterCampaign.endedAt,
                  '',
                  false,
                  false
                )}
              </Box.Text>
            )}
            {bookReadPromotionDetail && (
              <ReadFree
                chapterId={data.id}
                price={data.price}
                isPaid={data.isPaid}
                isRestricted={isRestricted}
                bookReadPromotionDetail={bookReadPromotionDetail}
              />
            )}
          </div>
          <Box.Text
            className={cn('text-sm w-full flex mt-[10px] mobile:mt-[8px]', {
              'text-primary font-bold': data.isBookmarked,
              'opacity-30': isRestricted,
            })}
          >
            {data.isBookmarked && (
              <BookmarkSolidIcon
                className='mr-[10px] min-w-[18px] min-h-[18px]'
                width='18'
                height='18'
              />
            )}
            {/*  <span className='mr-[28px] line-clamp-1 max-w-[40px] w-full mobile:mr-[10px] mobile:max-w-[20px]'> */}
            {/*  {data.chapterTitle} */}
            {/* </span> */}
            <span className='line-clamp-1 break-all leading-[23px] dark:text-dark-10'>
              {data.chapterTitle} {data.chapterName}
            </span>
          </Box.Text>
        </div>
      </div>

      <div className='flex w-full items-center mobile:mt-[10px]'>
        <Box.Text
          className={cn(
            'text-sm w-[65px] mr-[20px] text-secondary-1 tablet:hidden whitespace-nowrap shrink-0',
            {
              'opacity-30': isRestricted,
              'mr-[15px]': isDrawer,
            }
          )}
        >
          {formatDateDomini(data.updatedAtOrPublishedAt, false, false)}
        </Box.Text>
        <Box.Text
          className={cn(
            'flex items-center mr-[20px] text-sm w-[65px] text-secondary-1 shrink-0',
            {
              'opacity-30': isRestricted,
              'mr-[15px]': isDrawer,
            }
          )}
        >
          <EyeIcon width='18' height='18' className='text-primary mr-[5px]' />
          {abbreviateNumber(data.viewCount)}
        </Box.Text>
        <Box.Text
          className={cn(
            'flex items-center mr-[20px] text-sm w-[65px] text-secondary-1 shrink-0',
            {
              'opacity-30': isRestricted,
              'mr-[15px]': isDrawer,
            }
          )}
        >
          <ChatboxIcon
            width='18'
            height='18'
            className='text-primary mr-[5px]'
          />
          {abbreviateNumber(data.commentCount)}
        </Box.Text>

        {hasActiveBookChapterCampaign && !data.isPaid && !isDrawer && (
          <Box.Text
            className={cn(
              'h-fit whitespace-nowrap text-xs py-1 rounded px-2 text-white dark:text-white-2 text-center w-auto mobile:hidden',
              {
                'bg-red-3': data.chapterCampaign.sellingPrice > 0,
                'bg-blue-1': data.chapterCampaign.sellingPrice <= 0,
              }
            )}
          >
            {data.chapterCampaign.sellingPrice > 0 ? 'ลดราคา' : 'อ่านฟรี'}{' '}
            {promotionDate(
              data.chapterCampaign.startedAt,
              data.chapterCampaign.endedAt,
              '',
              false,
              false
            )}
          </Box.Text>
        )}

        <div
          className={cn('w-full flex pl-2', {
            'justify-end':
              !bookReadPromotionDetail ||
              (hasActiveBookChapterCampaign && isDrawer),
            'justify-between mobile:justify-end': !!bookReadPromotionDetail,
            'gap-2': hasActiveBookChapterCampaign && isDrawer,
          })}
        >
          {hasActiveBookChapterCampaign && !data.isPaid && isDrawer && (
            <div
              className={cn(
                'flex flex-col text-[14px] font-light mobile:hidden max-w-[100px]',
                {
                  'text-primary': data.chapterCampaign.sellingPrice <= 0,
                  'text-red': data.chapterCampaign.sellingPrice > 0,
                }
              )}
            >
              {data.chapterCampaign.sellingPrice > 0 ? 'ลดราคา' : 'อ่านฟรี'}{' '}
              {promotionDate(
                data.chapterCampaign.startedAt,
                data.chapterCampaign.endedAt,
                '',
                false,
                false
              )}
            </div>
          )}
          {bookReadPromotionDetail && (
            <ReadFree
              className={cn('mobile:hidden', {
                'max-w-[100px]': isDrawer,
              })}
              chapterId={data.id}
              price={data.price}
              isPaid={data.isPaid}
              isRestricted={isRestricted}
              bookReadPromotionDetail={bookReadPromotionDetail}
            />
          )}

          <Status
            price={data.price}
            isPaid={data.isPaid}
            isRestricted={isRestricted}
            isFree={isReadFreePromotion(data.id)}
            sellingPrice={data.chapterCampaign?.sellingPrice}
            hasChapterCampaign={hasActiveBookChapterCampaign}
          />
        </div>
      </div>
    </div>
  )
}
